import { Button, Label, Select } from "flowbite-react";
import { useState } from "react";
import { useGetWorkShiftsQuery, useCreateWorkShiftMutation } from "@/api/agentApi/workShift";
import { WorkShift } from "@/sharedTypes";
import WorkShiftFormDialog from "@/components/workShifts/WorkShiftFormDialog";
import { toast } from "@/lib/toast";
import { getErrorMessage } from "@/utils/requestUtils";
import { HiPlus } from "react-icons/hi";
import { useTranslation } from "react-i18next";

interface WorkShiftSelectProps {
  value?: string | number;
  onChange: (value: string) => void;
  error?: string;
  label?: string;
}

export default function WorkShiftSelect({
  value,
  onChange,
  error,
  label,
}: WorkShiftSelectProps) {
  const { t } = useTranslation(["workShift", "agent", "common"]);
  const displayLabel = label || t("agent:shift");

  const { data: shiftsData, isLoading } = useGetWorkShiftsQuery({
    pagination: { page: 1, pageSize: 100 },
    filters: { is_active: true },
  });

  // Handle data structure response
  const shifts: WorkShift[] = shiftsData?.items || shiftsData?.data || [];

  const [createWorkShift] = useCreateWorkShiftMutation();
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);

  const handleCreateShift = (data: any) => {
    createWorkShift(data)
      .unwrap()
      .then((res: any) => {
        toast.success(t("workShift:createSuccess"));
        setIsCreateDialogOpen(false);
        // The API returns the created object, hopefully with an ID
        const shiftId = res?.id || res?.data?.id;
        if (shiftId) {
          onChange(shiftId.toString());
        }
      })
      .catch((err) => toast.error(getErrorMessage(err)));
  };

  return (
    <div>
      <div className="mb-2 flex items-center justify-between">
        <Label value={displayLabel} />
        <Button
          size="xs"
          color="light"
          onClick={() => setIsCreateDialogOpen(true)}
          title={t("workShift:addNewWorkShift")}
        >
          <HiPlus className="h-4 w-4" />
        </Button>
      </div>
      <Select
        value={value?.toString() || ""}
        onChange={(e) => onChange(e.target.value)}
        color={error ? "failure" : "gray"}
        helperText={error}
        disabled={isLoading}
      >
        <option value="" disabled>
          {t("agent:selectShift")}
        </option>
        {shifts.map((shift: WorkShift) => (
          <option key={shift.id} value={shift.id}>
            {shift.shift_description}
          </option>
        ))}
      </Select>

      <WorkShiftFormDialog
        open={isCreateDialogOpen}
        onClose={() => setIsCreateDialogOpen(false)}
        onSubmit={handleCreateShift}
        mode="create"
      />
    </div>
  );
}
